import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { IMPORT_STATE, type RichpanelInfo } from '../types';

export const useRichPanel = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.RICH_PANEL,
    workspace_alias,
  );

  const { data } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.richpanel.getRichpanelInfo(workspace_alias),
    enabled: enabled,
  });

  const { mutate: disconnectRichpanelMutation } = useMutation({
    mutationFn: () => service.richpanel.deleteRichpanelInfo(workspace_alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.richpanel.importRichpanelFeedback(workspace_alias, dateRange),
    onSuccess: () => {
      const richpanelWorkspace = queryClient.getQueryData(
        queryKeys,
      ) as RichpanelInfo;
      queryClient.setQueryData(queryKeys, {
        ...richpanelWorkspace,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  return {
    richpanelWorkspace: data,
    disconnectRichpanelMutation,
    requestImportMutation,
  };
};
